import React, { useEffect, useState } from 'react';
import { ArrowRight, MailOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  fetchEngagementDashboardData,
  type EngagementDashboardData,
} from '../../lib/engagementDashboard';
import EngagementTrendChart from './EngagementTrendChart';

function loadErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return 'Failed to load engagements';
}

function StatBlock({
  label,
  value,
  sublabel,
  emphasize = false,
}: {
  label: string;
  value: number;
  sublabel?: string;
  emphasize?: boolean;
}) {
  return (
    <div className={emphasize ? '' : 'pt-2 mt-2 border-t border-gray-200/80 dark:border-gray-700/80'}>
      <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div
        className={`mt-0.5 font-bold tabular-nums text-gray-900 dark:text-white leading-none ${
          emphasize ? 'text-2xl' : 'text-lg'
        }`}
      >
        {value.toLocaleString()}
      </div>
      {sublabel ? (
        <div className="mt-0.5 text-[11px] text-gray-600 dark:text-gray-400">{sublabel}</div>
      ) : null}
    </div>
  );
}

const EngagementsDashboardPanel: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<EngagementDashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void fetchEngagementDashboardData()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(loadErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
      <div className="flex items-center justify-between gap-4 mb-2">
        <div className="flex items-center gap-2">
          <MailOpen className="w-4 h-4 text-sky-500" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Engagements</h2>
        </div>
        <button
          type="button"
          onClick={() => navigate('/outgoing-newsletters')}
          className="inline-flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
        >
          View all
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">Loading engagements…</div>
      ) : error ? (
        <div className="py-10 text-center text-sm text-rose-600 dark:text-rose-400">{error}</div>
      ) : data ? (
        <div className="flex flex-col md:flex-row gap-2 items-stretch">
          <div className="md:w-36 shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2.5">
            <StatBlock
              label="Queued"
              value={data.scheduledPending}
              sublabel="scheduled emails pending"
              emphasize
            />
            <StatBlock
              label="Next 7d"
              value={data.scheduledNext7Days}
              sublabel="due in the next week"
            />
          </div>

          <div className="md:w-56 shrink-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-3 py-2 min-w-0 overflow-hidden">
            <div className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
              Top clicks · 7d
            </div>
            {data.topClicks.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 py-2">No clicks in the last 7 days.</p>
            ) : (
              <ul className="divide-y divide-gray-200/80 dark:divide-gray-700/80">
                {data.topClicks.map((row) => (
                  <li key={row.url}>
                    <a
                      href={row.url.startsWith('http') ? row.url : undefined}
                      target={row.url.startsWith('http') ? '_blank' : undefined}
                      rel={row.url.startsWith('http') ? 'noreferrer' : undefined}
                      title={row.url}
                      className="flex items-center gap-2 py-1 text-left rounded hover:bg-white/80 dark:hover:bg-gray-800/80 transition-colors"
                    >
                      <span className="min-w-0 flex-1 text-xs text-gray-900 dark:text-gray-100 truncate">
                        {row.label}
                      </span>
                      <span className="shrink-0 inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold tabular-nums bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                        {row.clicks}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex-1 min-w-0 self-stretch rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-0 overflow-hidden flex flex-col">
            <EngagementTrendChart events={data.engagementEvents} className="h-full min-h-0 flex-1" />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default EngagementsDashboardPanel;
